function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary:not(.open_dropdown)').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        this.swiper = this.querySelector('.swiper');
        this.pagination = this.querySelector('.swiper-pagination');
        this.prev = this.querySelector('.swiper-button-prev');
        this.next = this.querySelector('.swiper-button-next');

        this.init();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  init(){
    new Swiper(this.swiper, {
      freeMode: true,
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 1.96,
          spaceBetween: 12,
        },
        750: {
          slidesPerView: 2.5,
          spaceBetween: 24,
        },
        1280: {
          slidesPerView: 3.8,
          spaceBetween: 32,
        },
        1600: {
          slidesPerView: 4,
          spaceBetween: 32,
        },
      }
    })
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class MegamenuSlideshow extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');

     this.init();
  }

  init(){
    this.slider = new Swiper(this.swiper, {
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 2.2,
          spaceBetween: 16,
        },
        990: {
          slidesPerView: 2.5,
          spaceBetween: 16,
        },
        1280: {
          slidesPerView: 3.5,
          spaceBetween: 24,
        },
        1600: {
          slidesPerView: 4.5,
          spaceBetween: 32,
        },
      }
    })
  }
}

customElements.define('megamenu-slideshow', MegamenuSlideshow);

class HerobannerSlideshow extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');
     this.autoplay = this.swiper.hasAttribute("data-autoplay") ? parseFloat(this.swiper.getAttribute("data-autoplay")) : 9999999;

     this.init();
  }

  init(){
    new Swiper(this.swiper, {
      slidesPerView: 1,
      autoplay: {
        delay: this.autoplay,
        pauseOnMouseEnter: false,
      },
      pagination: {
        el: this.pagination,
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
    })
  }
}

customElements.define('herobanner-slideshow', HerobannerSlideshow);


class MenuDrawerClose extends HTMLElement {
  constructor(){
    super();

    this.init();
    const resizeObserver = new ResizeObserver((entries) => this.init());
    resizeObserver.observe(this);
  }

  init(){
    this.addEventListener('click', () => {
      document.getElementById('drawer_menu_toggle').click();
    })
  }
}

customElements.define('drawer-close', MenuDrawerClose);

class ShopbrandSlideshow extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');

     this.init();
  }

  init(){
    this.slider = new Swiper(this.swiper, {
      freeMode: false,
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 1.2,
          spaceBetween: 21,
        },
        750: {
          slidesPerView: 2.5,
          spaceBetween: 21,
        },
        1280: {
          slidesPerView: 3.5,
          spaceBetween: 32,
        },
        1600: {
          slidesPerView: 3,
          spaceBetween: 32,
        },
      }
    })
  }
}

customElements.define('shop-brand', ShopbrandSlideshow);

class FooterAccordion extends HTMLElement {
  constructor(){
    super();

    this.button = this.querySelector('.footer_accordion_heading');
    this.contents = this.querySelector('.footer_accordion_content');

    this.button.addEventListener('click', this.toggleContent.bind(this));
  }

  toggleContent(event){
    event.target.classList.toggle('active');
  }
}

customElements.define('footer-accordion', FooterAccordion);


class FeaturedCollectionSlideshow extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');

     this.init();
  }

  init(){
    this.slider = new Swiper(this.swiper, {
      freeMode: false,
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 1.96,
          spaceBetween: 12,
        },
        750: {
          slidesPerView: 2.5,
          spaceBetween: 24,
        },
        1280: {
          slidesPerView: 3.8,
          spaceBetween: 32,
        },
        1600: {
          slidesPerView: 4,
          spaceBetween: 32,
        },
      }
    })
  }
}

customElements.define('featured-collection-slideshow', FeaturedCollectionSlideshow);


class SearchProductSlider extends HTMLElement {
  constructor() {
    super();

    this.swiper = this.querySelector('.swiper');
    this.pagination = this.querySelector('.swiper-pagination');
    this.prev = this.querySelector('.swiper-button-prev');
    this.next = this.querySelector('.swiper-button-next');

    // Initial check for viewport size
    if (window.innerWidth < 750) {
      this.initSlider();
    }

    window.addEventListener('resize', () => this.handleResize());
  }

  initSlider() {
    if (!this.swiper) return;

    this.slider = new Swiper(this.swiper, {
      freeMode: false,
      enabled: true,
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      slidesPerView: 2,
      spaceBetween: 14,
      breakpoints: {
        320: {
          slidesPerView: 2,
          spaceBetween: 14,
        },
        520: {
          slidesPerView: 2,
          spaceBetween: 24,
        }
      }
    });
  }

  handleResize() {
    const isMobile = window.innerWidth < 768;

    if (!isMobile) {
      if (this.slider && !this.slider.destroyed) {
        this.slider.destroy(true, true);
      }
    } else {
      if (!this.slider || (this.slider && this.slider.destroyed)) {
        this.initSlider();
      }
    }
  }
}

customElements.define('search-product-slider', SearchProductSlider);

class CtegoryRegionSlideshow extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');

     this.init();
  }

  init(){
    this.slider = new Swiper(this.swiper, {
      freeMode: false,
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 3,
          spaceBetween: 12,
        },
        750: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
        1280: {
          slidesPerView: 2,
          spaceBetween: 32,
        },
        1600: {
          slidesPerView: 2,
          spaceBetween: 32,
        },
      }
    })
  }
}

customElements.define('category-region-slideshow', CtegoryRegionSlideshow);


class CustomTab extends HTMLElement {
  constructor(){
    super();

    this.buttons = this.querySelectorAll('.tab_btn');
    this.tabContents = this.querySelectorAll('.tab_content');

    this.buttons.forEach(btn => {
      btn.addEventListener("click", this.init.bind(this));
    })
  }

  init(event){
    let activeContent = event.target.getAttribute('data-target');
    this.buttons.forEach(btn => btn.classList.remove('active'));
    this.tabContents.forEach(content => content.classList.remove('active'));

    event.target.classList.add('active');
    this.querySelector('.' + activeContent).classList.add('active');
  }
}

customElements.define('custom-tab', CustomTab);

class FilterSpirits extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');

     this.tags = this.querySelectorAll('.spirits_item');
     this.summary = this.querySelector('summary');
     this.activeText = this.summary.querySelector('span');

     this.init();

     if (this.tags.length) this.filter(this.tags[0], false);
     this.tags.forEach(tag => {
      tag.addEventListener("click", ()=>this.filter(tag));
    })
  }

  filter(target, updateText = true) {
    this.querySelector(".swiper-wrapper").style.transform = "translate3d(0px, 0px, 0px)";
    let activeTag = target.getAttribute('data-tag');
    let activeTagText = target.innerText.trim();

    this.tags.forEach(tag => tag.classList.remove('active'))
    target.classList.add('active');
    if (updateText) {
      this.activeText.innerHTML = activeTagText;
      this.summary.click();
    }

    // toggle block content
    this.querySelectorAll('.swiper-slide').forEach(slide => {
      slide.classList.add('hidden')
      slide.classList.remove('swiper-slide')
    });

    this.querySelectorAll('.' + activeTag).forEach(item => {
      item.classList.remove('hidden');
      item.classList.add('swiper-slide');
      this.init();
    });
  }

  init(){
    this.slider = new Swiper(this.swiper, {
      freeMode: false,
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 2.1,
          spaceBetween: 12,
        },
        480: {
          slidesPerView: 2.1,
          spaceBetween: 12,
        },
        750: {
          slidesPerView: 2.1,
          spaceBetween: 24,
        },
        990: {
          slidesPerView: 2.1,
          spaceBetween: 24,
        },
        1365: {
          slidesPerView: 3.4,
          spaceBetween: 32,
        },
        1600: {
          slidesPerView: 3.9,
          spaceBetween: 32,
        },
      }
    })
  }
}

customElements.define('filter-spirits', FilterSpirits);

class TrendingBanner extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');

     this.init();
  }

  init(){
    this.slider = new Swiper(this.swiper, {
      slidesPerView: 1,
      spaceBetween: -1,
      pagination: {
        el: this.pagination,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev
      }
    })
  }
}

customElements.define('trending-banner', TrendingBanner);

class TrendingProducts extends HTMLElement {
  constructor(){
    super();

     this.swiper = this.querySelector('.swiper');
     this.pagination = this.querySelector('.swiper-pagination');
     this.prev = this.querySelector('.swiper-button-prev');
     this.next = this.querySelector('.swiper-button-next');

     const resizeObserver = new ResizeObserver(entries => this.init());
    resizeObserver.observe(this.swiper);
     this.init();
  }

  init(){
    if(window.innerWidth < 1280) {
      this.slider = new Swiper(this.swiper, {
        freeMode: false,
        pagination: {
          el: this.pagination,
          type: "progressbar",
        },
        navigation: {
          nextEl: this.next,
          prevEl: this.prev,
        },
        breakpoints: {
          0: {
            slidesPerView: 2,
            spaceBetween: 12,
          },
          750: {
            slidesPerView: 1,
            spaceBetween: 24,
          },
          800: {
            slidesPerView: 1.2,
            spaceBetween: 24,
          },
          1024: {
            slidesPerView: 1.8,
            spaceBetween: 24,
          },
        }
      })
    } else {
      this.slider = null;
    }
  }
}

customElements.define('trending-products', TrendingProducts);

class SaleCountdownTimer extends HTMLElement {
  constructor(){
    super();
    this.date = this.dataset.date;
    this.timer = this.querySelector(".custom-sale-countdown-timer");
    this.init();
  }

  init(){
    let countDownDate = new Date(this.date).getTime();
    let timer = this.timer;

    let timerInterval = setInterval(function() {
      var now = new Date().getTime();
      var distance = countDownDate - now;
      var days = Math.floor(distance / (1000 * 60 * 60 * 24));
      var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      var seconds = Math.floor((distance % (1000 * 60)) / 1000);

      timer.innerHTML = `
      <span>${days < 10 && days > 0 ? "0" : ""}${days} <i>Days</i></span><em>:</em>
      <span>${hours < 10 && hours > 0 ? "0" : ""}${hours} <i>Hours</i></span><em>:</em>
      <span>${minutes < 10 && minutes > 0 ? "0" : ""}${minutes} <i>Min</i></span><em>:</em>
      <span>${seconds < 10 && seconds > 0 ? "0" : ""}${seconds} <i>Sec</i></span>`;

      if (distance < 0) {
        clearInterval(timerInterval);
        timer.innerHTML = "";
      }
    }, 1000);
  }
}

customElements.define('sale-countdown-timer', SaleCountdownTimer);


// LoadMoreProducts component
class LoadMoreProducts extends HTMLElement {
  constructor(){
    super();
    this.wrapper = this.querySelector(".custom-load-more-wrapper");
    this.buttonWrapper = this.querySelector(".custom-load-more-button-wrapper");
    this.button = this.querySelector(".custom-load-more-button");
    this.init();
  }

  init(){
    let wrapper = this.wrapper;
    let button = this.button;
    let buttonWrapper = this.buttonWrapper;
    button.addEventListener("click", function(e){
      button.classList.add("active");
      let url = button.dataset.url;
      if(url != null && url != undefined){
        fetch(url)
        .then(res => res.text())
        .then(res => {
          let html = new DOMParser().parseFromString(res, 'text/html');
          if (button.dataset.id!=undefined){
            // console.log(document.getElementById(button.dataset.id), html.getElementById(button.dataset.id).childNodes)
            
            document.getElementById(button.dataset.id).append(...html.getElementById(button.dataset.id).childNodes);
            button.setAttribute("data-url", html.querySelector(".custom-load-more-button-wrapper>button").dataset.url);
          }
          else {
            wrapper.insertAdjacentHTML('beforeend', html.querySelector(".custom-load-more-wrapper").innerHTML);
            button.setAttribute("data-url", html.querySelector(".custom-load-more-wrapper").dataset.url);
          }
        })
        .catch(err => {
          console.log(err, "Error LoadMoreProducts");
          buttonWrapper.style.display = "none";
          button.classList.remove("active");
        })
        .finally(() => {
          button.classList.remove("active");
        })
      }
      else{
        buttonWrapper.style.display = "none";
        button.classList.remove("active");
      }
    })
  }
}

customElements.define('load-more-products', LoadMoreProducts);

document.addEventListener('DOMContentLoaded', function () {
  const asideFilterBtn = document.querySelector('.collection-custom-facets-wrapper');
  if(asideFilterBtn){
  const asideFilterBtnOffsetTop = asideFilterBtn.offsetTop;


  window.addEventListener('scroll', function () {
      if (window.scrollY >= asideFilterBtnOffsetTop) {
          asideFilterBtn.classList.add('reached-topview');
      } else {
          asideFilterBtn.classList.remove('reached-topview');
      }
  });
  }

});


class CartRelatedProducts extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.loadRecommendations(this.dataset.productId);
  }
  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-related-products');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-related-products', CartRelatedProducts);

class CartComplementaryProducts extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.loadRecommendations(this.dataset.productId);
  }
  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-complementary-products');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
        this.swiper = this.querySelector('.swiper');
        this.scrollbar = this.querySelector('.swiper-scrollbar');
        this.prev = this.querySelector('.swiper-button-prev');
        this.next = this.querySelector('.swiper-button-next');
        this.init();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  init(){
    new Swiper(this.swiper, {
      freeMode: true,
      slidesPerView: 1.8,
      spaceBetween: 32,
      scrollbar: {
        el: this.scrollbar
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 1.4,
          spaceBetween: 16,
        },
        1400: {
          slidesPerView: 1.8,
          spaceBetween: 32,
        }
      }
    })
  }
}

customElements.define('cart-complementary-products', CartComplementaryProducts);

document.addEventListener("click", function(e){
  if(e.target.classList.contains("custom-gift-button-inner") || e.target.classList.contains("custom-gift-drawer-close")){
    e.target.closest(".drawer").classList.toggle("gift-active");
  }
});

class GiftCollection extends HTMLElement {
  constructor(){
    super();
    this.swiper = this.querySelector('.swiper');
    this.scrollbar = this.querySelector('.swiper-scrollbar');
    this.prev = this.querySelector('.swiper-button-prev');
    this.next = this.querySelector('.swiper-button-next');
    this.init();
  }

  init(){
    new Swiper(this.swiper, {
      freeMode: true,
      slidesPerView: 1.8,
      spaceBetween: 32,
      scrollbar: {
        el: this.scrollbar
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 1.4,
          spaceBetween: 16,
        },
        1400: {
          slidesPerView: 1.8,
          spaceBetween: 32,
        }
      }
    })
  }
}

customElements.define('gift-collection', GiftCollection);

class GiftCardCollection extends HTMLElement {
  constructor(){
    super();
    this.swiper = this.querySelector('.swiper');
    this.scrollbar = this.querySelector('.swiper-scrollbar');
    this.prev = this.querySelector('.swiper-button-prev');
    this.next = this.querySelector('.swiper-button-next');
    this.init();
  }

  init(){
    new Swiper(this.swiper, {
      freeMode: true,
      slidesPerView: 3,
      spaceBetween: 24,
      scrollbar: {
        el: this.scrollbar
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
      breakpoints: {
        0: {
          slidesPerView: 2.5,
          spaceBetween: 20,
        },
        430: {
          slidesPerView: 2.5,
          spaceBetween: 20,
        },
        1400: {
          slidesPerView: 3,
          spaceBetween: 24,
        }
      }
    })
  }
}

customElements.define('gift-card-collection', GiftCardCollection);

class GiftMessage extends HTMLElement {
  constructor(){
    super();

    this.form = this.querySelector(".custom-gift-message-form");
    this.input = this.querySelector('.custom-gift-message');
    this.button = this.querySelector('.custom-gift-message-btn');
    this.spinner = this.querySelector(".loading__spinner");
    this.cart = this.closest(".drawer");

    this.submit(this.form, this.input, this.button, this.spinner, this.cart);
  }

  submit(form, input, button, spinner, cart){
    form.addEventListener("submit", function(e){
      e.preventDefault();
      let formData = new FormData();
      formData.append(input.getAttribute("name"), input.value);
      button.classList.add("loading");
      spinner.classList.remove("hidden");
      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        body: formData
      })
      .then(function(res){
        if(res.ok){
          cart.classList.remove("gift-active");
          if(input.value.trim() != ''){
            cart.querySelector(".custom-gift-button-message").innerHTML = `<em>Gift message:</em> <span>${input.value}</span>`;
          }
          else{
            cart.querySelector(".custom-gift-button-message").innerHTML = '';
          }
        }
        else{
          console.log(res, "Gift Message Error!");
        }
      })
      .finally(function(){
        button.classList.remove("loading");
        spinner.classList.add("hidden");
      });
    })
    button.addEventListener("click", function(){
      form.dispatchEvent(new Event("submit"));
    })
  }
}

customElements.define('gift-message', GiftMessage);

class UpcomingEvents extends HTMLElement {
  constructor(){
    super();
    this.month = this.querySelectorAll(".upcoming-events-month-input");
    this.year = this.querySelectorAll(".upcoming-events-year-selector-input");
    this.monthSelectedWrapper = this.querySelector(".upcoming-events-selected-month");
    this.yearSelector = this.querySelector(".upcoming-events-year-selector");
    this.yearSelectorText = this.querySelector(".upcoming-events-year-selector-summary span");
    this.contentWrapper = this.querySelector(".upcoming-events-content-wrapper");
    this.item = this.querySelectorAll(".upcoming-events-content-item");
    
    this.init();
    this.updateActiveItems();
  }

  init(){
    let $this = this;
    
    this.month.forEach(month => {
      month.addEventListener("change", function(){
        $this.updateActiveItems();
      });
    });

    this.year.forEach(year => {
      year.addEventListener("click", function(){
        $this.updateActiveItems();
      });
    });
  }

  updateActiveItems(){
    let year = this.querySelector('.upcoming-events-year-selector-input:checked').value.trim();
    let month = this.querySelector('.upcoming-events-month-input:checked').value.trim();
    let el = `.upcoming-events-content-item[data-year="${year}"][data-month="${month}"]`;

    this.item.forEach(item => item.classList.remove("active", "upcoming-events-content-item-first", "upcoming-events-content-item-last"));
    
    let activeItems = Array.from(this.querySelectorAll(el));

    if (activeItems.length > 0) {
      // Sort by `data-day` in ascending order
      activeItems.sort((a, b) => parseInt(a.dataset.day) - parseInt(b.dataset.day));

      // Reorder elements in the DOM
      activeItems.forEach(item => this.contentWrapper.appendChild(item));

      // Add the "active" class and assign first & last
      activeItems.forEach(item => item.classList.add("active"));
      activeItems[0].classList.add("upcoming-events-content-item-first");
      activeItems[activeItems.length - 1].classList.add("upcoming-events-content-item-last");
    }

    this.monthSelectedWrapper.innerText = this.querySelector('.upcoming-events-month-input:checked').getAttribute("data-full-month");
    this.yearSelectorText.innerText = year;
  }
}

customElements.define('upcoming-events', UpcomingEvents);

if(document.querySelector(".facet-filters__sort")){
document.querySelector(".facet-filters__sort").addEventListener("change", function(){
  let url = location.href;
  let el = document.querySelector("#FacetSortDrawerForm");
  let final_url = "";
  if(url.indexOf("&sort_by=") != -1){
    final_url = `${el.dataset.handle}?${url.split("?")[1].split("&sort_by=")[0]}&sort_by=${this.value}`;
  }
  if(final_url == ""){
    this.closest("form").submit();
  }
  else{
    location.href = final_url;
  }
})
}

document.addEventListener("click", function(e){
  if(e.target.closest("summary")){
    let summary = e.target.closest("summary");
    if(summary.closest(".product__accordion")){
      e.preventDefault();
      let details = summary.closest("details");
      let content = details.querySelector(".accordion__content") || details.querySelector(".complementary--accordion");
      let height = content.scrollHeight;
      if(details.classList.contains("active")){
        details.classList.remove("active");
        content.setAttribute("style", "height: 0px");
      }
      else{
        details.classList.add("active");
        content.setAttribute("style", `height: ${height}px`);
      }
    }
  }
});



/*setting default availability filter for PLP */
/*setting default availability filter for PLP and Search */
function updateCollectionLinks() {
  // Only select collection links to improve performance
  const links = document.querySelectorAll('a[href*="/collections/"]');

  links.forEach(link => {
      try {
          const url = new URL(link.href);
          
          // Check if parameter doesn't exist
          if (!url.searchParams.has('filter.v.availability')) {
              url.searchParams.set('filter.v.availability', '1');
              link.href = url.toString();
          }
      } catch (error) {
          console.error('Error processing link:', link.href, error);
      }
  });
}

// Run on initial load
document.addEventListener('DOMContentLoaded', updateCollectionLinks);

class AccountSlider extends HTMLElement {
  constructor(){
    super();
    this.swiper = this.querySelector('.swiper');
    this.pagination = this.querySelector('.swiper-pagination');
    this.prev = this.querySelector('.swiper-button-prev');
    this.next = this.querySelector('.swiper-button-next');
    this.init();
  }

  init(){
    new Swiper(this.swiper, {
      slidesPerView: 1,
      pagination: {
        el: this.pagination,
      },
      navigation: {
        nextEl: this.next,
        prevEl: this.prev,
      },
    })
  }
}

customElements.define('account-slider', AccountSlider);



if(document.querySelector(".custom-account-pass-toggle")){
  document.querySelectorAll(".custom-account-pass-toggle").forEach(function(el){
    el.addEventListener("click", function(){
      let input = this.closest(".field").querySelector("input");
      input.getAttribute("type") == 'password' ? input.setAttribute("type", "text") : input.setAttribute("type", "password");
      this.closest(".custom-account-pass-toggle-wrapper").classList.toggle("active");
    })
  })
}



/*Placeholder animation for the search input */
class SearchPlaceholderTypeWriter {
  constructor(inputId, words, wait = 2000) {
    this.input = document.getElementById(inputId);
    if (!this.input) return;

    this.words = words;
    this.wait = wait;
    this.txt = '';
    this.wordIndex = 0;
    this.isDeleting = false;
    this.type();
  }

  type() {
    const currentWord = this.words[this.wordIndex];

    if (this.isDeleting) {
      this.txt = currentWord.substring(0, this.txt.length - 1);
    } else {
      this.txt = currentWord.substring(0, this.txt.length + 1);
    }

    this.input.setAttribute('placeholder', this.txt);

    let typeSpeed = this.isDeleting ? 60 : 100;

    if (!this.isDeleting && this.txt === currentWord) {
      typeSpeed = this.wait;
      this.isDeleting = true;
    } else if (this.isDeleting && this.txt === '') {
      this.isDeleting = false;
      this.wordIndex = (this.wordIndex + 1) % this.words.length;
    }

    setTimeout(() => this.type(), typeSpeed);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const placeholders = [
    "Find What's On Your Mind?",
    "Search for products...",
    "What are you looking for?",
    "Discover our collection..."
  ];

  new SearchPlaceholderTypeWriter('Search-In-Modal', placeholders);
});



// showing megamenu over hovering
const inlineMenu = document.querySelector(".header__inline-menu");
const detailsItems = inlineMenu.querySelectorAll("details");
 
detailsItems.forEach(item => {
  const ulElement = item.querySelector("ul");
  const closeBtn = item.querySelector(".megamenu-close-btn svg");
  
  // Open menu on hover
  item.addEventListener("mouseover", () => {
    item.setAttribute("open", true);
    
    // Close menu when mouse leaves the ul element
    ulElement.addEventListener("mouseleave", () => {
      item.removeAttribute("open");
    });
    
    // Close menu when mouse leaves the details element
    item.addEventListener("mouseleave", () => {
      item.removeAttribute("open");
    });
  });
  // Add click event for close button
  if (closeBtn) {
    closeBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      item.removeAttribute("open");
    });
  }
  
});

//plus & minus icon interchanges within PLP Filters drawer for desktop
document.addEventListener('DOMContentLoaded', function() {
  const details = document.querySelectorAll('.drawer_mobile-facets__details');
  

  details.forEach((detail, index) => {
      const plusIcon = detail.querySelector('.filter-plus');
      
      if (!plusIcon) {
          return;
      }
      
      detail.addEventListener('toggle', function() {
          console.log('Details toggled:', this.open);
          if (this.open) {
              plusIcon.classList.add('filter-minus');
              
          } else {
              plusIcon.classList.remove('filter-minus');
              
          }
      });
      
  });
});


  // Function to update the product count
  function updateProductCount() {
    var productCountElement = document.getElementById('ProductCount');
    
    if (!productCountElement) return;

    var baseTotal = parseInt(productCountElement.getAttribute('data-total'));
    var hasLtoWrapper = document.querySelector('#product-grid .custom-lto-wrapper') !== null;
    
    // Add 1 to total if LTO wrapper exists
    var totalProducts = hasLtoWrapper ? baseTotal + 1 : baseTotal;
    
    var currentProductCount = document.querySelectorAll('#product-grid .grid__item').length;
    
    // Determine the starting number based on whether there are any grid items
    var startingNumber = currentProductCount === 0 ? 0 : 1;

    // Update the product count display
    productCountElement.innerHTML = `Showing <span class="ProductCount-numbers">${startingNumber}-${currentProductCount}</span> out of <span class="ProductCount-numbers">${totalProducts}</span>`;
  }
  document.addEventListener('DOMContentLoaded', function() {
    // Run the update function
    updateProductCount();
  });



//bottom to top sticky filter bar for desktop
let lastScrollTop = 0;
window.addEventListener('scroll', function() {
    let currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const filterBar = document.querySelector('.collection-custom-facets-wrapper');

    if(filterBar){
    if (currentScrollTop < lastScrollTop) {
        // Scrolling up
        filterBar.classList.add('filter-bar-scrollup');
    } else {
        // Scrolling down
        filterBar.classList.remove('filter-bar-scrollup');
    }
    
    lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
    }
});



//price range slider for filters
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeInput = this.querySelectorAll(".range-input input"),
    this.priceInput = this.querySelectorAll(".price-input input"),
    this.range = this.querySelector(".slider .progress");
    this.priceGap = 10;
    this.init(this.rangeInput, this.priceInput, this.range, this.priceGap);
  }

  init(rangeInput, priceInput, range, priceGap){
    console.log("init price range");
    priceInput.forEach((input) => {
      input.addEventListener("input", (e) => {
        console.log("testInput");
        let minPrice = parseInt(priceInput[0].value),
          maxPrice = parseInt(priceInput[1].value);

        if (maxPrice - minPrice >= priceGap && maxPrice <= rangeInput[1].max) {
          if (e.target.className === "input-min") {
            rangeInput[0].value = minPrice;
            range.style.left = (minPrice / rangeInput[0].max) * 100 + "%";
          } else {
            rangeInput[1].value = maxPrice;
            range.style.right = 100 - (maxPrice / rangeInput[1].max) * 100 + "%";
          }
        }
      });
    });


    rangeInput.forEach((input) => {
      input.addEventListener("input", (e) => {
        console.log("testRange");
        let minVal = parseInt(rangeInput[0].value),
          maxVal = parseInt(rangeInput[1].value);

        if (maxVal - minVal < priceGap) {
          if (e.target.className === "range-min") {
            rangeInput[0].value = maxVal - priceGap;
          } else {
            rangeInput[1].value = minVal + priceGap;
          }
        } else {
          priceInput[0].value = minVal;
          priceInput[1].value = maxVal;
          range.style.left = (minVal / rangeInput[0].max) * 100 + "%";
          range.style.right = 100 - (maxVal / rangeInput[1].max) * 100 + "%";
        }
      });
    });
  }
}

customElements.define('price-range', PriceRange);

  document.addEventListener("click", function(e){
    if(!e.target.classList.contains('custom-add-more-title')){
      document.querySelectorAll(".custom-add-more-wrapper").forEach(function(item){
        item.classList.remove("active");
      });
    }
    else{
      if(e.target.closest(".custom-add-more-wrapper").classList.contains("active")){
        e.target.closest(".custom-add-more-wrapper").classList.toggle("active");
      }
      else{
        document.querySelectorAll(".custom-add-more-wrapper").forEach(function(items){
          items.classList.remove("active");
        });
        e.target.closest(".custom-add-more-wrapper").classList.add("active");
      }
    }
    if(e.target.classList.contains("custom-add-more-dropdown-list")){
      let qty = parseInt(e.target.dataset.qty);
      let input = e.target.closest(".cart-item__quantity").querySelector(".quantity__input");
      let curreInputQty = parseInt(input.value);
      input.value = curreInputQty + qty;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  })

  function onSwymLoadCallback(swat) {
    let eventString = 'sw:addedtocart';
    let listener = function(e) { 
      fetch(window.location.pathname + "?sections=cart-drawer,cart-icon-bubble")
      .then(res => res.json())
      .then(res => {
        if(res.status){
          console.log(res, "wishlist cart drawer update error!");
        }
        else{
          let cartParsedData = new DOMParser().parseFromString(res['cart-drawer'], 'text/html');
          let cartBubbleParsedData = new DOMParser().parseFromString(res['cart-icon-bubble'], 'text/html');
          document.querySelector('cart-drawer.drawer').classList.remove("is-empty");
          document.querySelector('.drawer__inner').innerHTML = cartParsedData.querySelector('.drawer__inner').innerHTML;
          document.querySelector('#cart-icon-bubble').innerHTML = cartBubbleParsedData.querySelector('.shopify-section').innerHTML;
        }
      })
    };
    let eventString2 = 'sw:addedalltocart';
    let listener2 = function(e) {
      fetch(window.location.pathname + "?sections=cart-drawer,cart-icon-bubble")
      .then(res => res.json())
      .then(res => {
        if(res.status){
          console.log(res, "wishlist cart drawer update error!");
        }
        else{
          let cartParsedData = new DOMParser().parseFromString(res['cart-drawer'], 'text/html');
          let cartBubbleParsedData = new DOMParser().parseFromString(res['cart-icon-bubble'], 'text/html');
          document.querySelector('cart-drawer.drawer').classList.remove("is-empty");
          document.querySelector('.drawer__inner').innerHTML = cartParsedData.querySelector('.drawer__inner').innerHTML;
          document.querySelector('#cart-icon-bubble').innerHTML = cartBubbleParsedData.querySelector('.shopify-section').innerHTML;
        }
      })
    };
    swat.evtLayer.addEventListener(eventString, listener);
    swat.evtLayer.addEventListener(eventString2, listener2);
  }
  if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
  }
  window.SwymCallbacks.push(onSwymLoadCallback);

class AccountReactivation {
  constructor() {
    this.form = document.querySelector('[data-reactivate-form]');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[data-reactivate-submit]');
    this.emailInput = this.form.querySelector('#reactivateEmail');

    this.init();
  }

  init() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.emailInput.value || !this.validateEmail(this.emailInput.value)) {
      this.showToast('Please enter a valid email address', 'error');
      return;
    }

    this.setLoading(true);

    try {
      const response = await this.reactivateAccount(this.emailInput.value);
      if (response.success) {
        this.showToast(response.message, 'success');
        this.form.reset();
      } else {
        this.showToast(response.message, 'error');
      }
    } catch (error) {
      console.error('Reactivation error:', error);
      this.showToast(error.message, 'error');
    } finally {
      this.setLoading(false);
    }
  }

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async reactivateAccount(email) {
    const dev_url = "https://craft-cellars-39b6aef8ca6d.herokuapp.com/customerSendAccountInviteEmailDev";
    const prod_url = "https://craft-cellars-39b6aef8ca6d.herokuapp.com/customerSendAccountInviteEmail";

    const url = window.origin === 'https://craft-cellars-dev.myshopify.com' ? dev_url : prod_url;
    
    try {
      const response = await fetch(`${url}?email=${email}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
      });

      const data = await response.json();
      console.log('Server response:', data); // Move console.log here
      
      if (!response.ok) {
        throw {
          success: false,
          message: data.error
        };
      }

      return {
        success: true,
        message: data.message || 'Reactivation email sent successfully!'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to send reactivation email'
      };
    }
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.submitButton.classList.add('loading');
    } else {
      this.submitButton.classList.remove('loading');
    }
  }

  showToast(message, type = 'success') {
    let container = document.querySelector('.toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'toast-container';
      document.body.appendChild(container);
    }

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    
    const icon = type === 'success' 
      ? '<svg class="toast-icon" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>'
      : '<svg class="toast-icon" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>';

    toast.innerHTML = `
      ${icon}
      <div class="toast-content">${message}</div>
      <button class="toast-close" aria-label="Close notification">
        <svg width="12" height="12" viewBox="0 0 12 12">
          <path fill="currentColor" d="M6 4.94L10.94 0L12 1.06L7.06 6L12 10.94L10.94 12L6 7.06L1.06 12L0 10.94L4.94 6L0 1.06L1.06 0L6 4.94z"/>
        </svg>
      </button>
    `;

    container.appendChild(toast);

    // Add close button functionality
    const closeButton = toast.querySelector('.toast-close');
    closeButton.addEventListener('click', () => {
      toast.classList.add('hide');
      setTimeout(() => toast.remove(), 300);
    });

    // Show toast
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.add('hide');
        setTimeout(() => toast.remove(), 300);
      }
    }, 5000);
  }
}

// Initialize account reactivation
document.addEventListener('DOMContentLoaded', () => {
  new AccountReactivation();
});


// Updating wishlist page continue shopping button URL
document.addEventListener("DOMContentLoaded", ()=>{
  console.log("SWYM button update initiated!")
  function updateSwymBtn(swymEmptyBtn) {
    let newSwymEmptyBtn = document.createElement("a");
    newSwymEmptyBtn.href = "/collections/all?filter.v.availability=1";
    newSwymEmptyBtn.innerHTML = swymEmptyBtn.innerHTML;
    for (let attr of swymEmptyBtn.attributes) {
      newSwymEmptyBtn.setAttribute(attr.name, attr.value);
    }

    swymEmptyBtn.parentElement.insertBefore(newSwymEmptyBtn, swymEmptyBtn);
    document.querySelector(".swym-empty-wishlist-container").classList.add("initiated");
  }
  let swymEmptyBtn = null;

  let swymBtnChecker = setInterval(() => {
    swymEmptyBtn = document.querySelector(".swym-empty-wishlist-continue-btn");
    if (swymEmptyBtn) {
      setTimeout(()=>{
        updateSwymBtn(swymEmptyBtn);
      }, 1500)
      clearInterval(swymBtnChecker);
      console.log("SWYM button Updated");
    } else {
      console.log("SWYM button not found");
    }
  }, 500); 
})